import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Upload, Loader2, Trash2, Save, Sigma } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

export default function DataStatsPanel({ userId, projectId, onTableSaved }) {
  const [datasets, setDatasets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeId, setActiveId] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await thesisApi.listDatasets(userId, projectId);
      setDatasets(rows);
      if (rows.length && !rows.find((d) => d.id === activeId)) setActiveId(rows[0].id);
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  const active = datasets.find((d) => d.id === activeId);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const ds = await thesisApi.uploadDataset(userId, projectId, file);
      toast.success(`Dataset loaded: ${ds.n_rows} rows · ${ds.columns.length} columns`);
      setResult(null);
      setGroupBy('');
      await load();
      setActiveId(ds.id);
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleCompute = async () => {
    if (!activeId) return;
    setComputing(true);
    try {
      const data = await thesisApi.describeDataset(userId, activeId, groupBy || null);
      setResult(data);
      toast.success('Statistics computed');
    } catch (e) {
      toast.error('Computation failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setComputing(false);
    }
  };

  const handleSaveAsTable = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const t = await thesisApi.saveTable(userId, projectId, {
        caption: result.caption, markdown: result.markdown,
        note: result.note, source: 'statistics',
      });
      toast.success(`Saved as Table ${t.number}`);
      onTableSaved?.();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dataset?')) return;
    try {
      await thesisApi.deleteDataset(userId, id);
      setResult(null);
      load();
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Card data-testid="data-stats-panel">
      <CardHeader>
        <CardTitle className="font-serif flex items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <BarChart3 className="w-5 h-5 text-[#1d4ed8]" /> Data & statistics
          <Badge variant="outline" className="text-[10px]">CSV / XLSX</Badge>
        </CardTitle>
        <CardDescription>
          Upload your study dataset and generate a Table 1 — mean ± SD, median [IQR], range, n (%),
          plus group comparisons (Welch t-test, ANOVA, chi-square). Numbers come from your data, never from the AI.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="cursor-pointer block">
          <input type="file" accept=".csv,.tsv,.xlsx,.xls" className="hidden"
            onChange={handleUpload} disabled={uploading} data-testid="dataset-upload-input" />
          <Button asChild disabled={uploading} className="w-full bg-[#0b1a35] hover:bg-[#1d4ed8] text-white">
            <span>{uploading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
              : <><Upload className="w-4 h-4 mr-2" />Upload dataset</>}</span>
          </Button>
        </label>

        {datasets.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[180px]"
                value={activeId} onChange={(e) => { setActiveId(e.target.value); setResult(null); setGroupBy(''); }}
                data-testid="dataset-select">
                {datasets.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.n_rows} rows)</option>)}
              </select>
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[160px]"
                value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
                data-testid="dataset-groupby-select">
                <option value="">No grouping (descriptive)</option>
                {(active?.columns || []).filter((c) => c.kind === 'categorical' && c.levels > 1 && c.levels <= 6)
                  .map((c) => <option key={c.name} value={c.name}>Compare by: {c.name}</option>)}
              </select>
              <Button onClick={handleCompute} disabled={computing || !activeId}
                data-testid="dataset-compute-btn">
                {computing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Computing...</>
                  : <><Sigma className="w-4 h-4 mr-2" />Compute statistics</>}
              </Button>
              {active && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(active.id)}
                  data-testid="dataset-delete-btn">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              )}
            </div>

            {active && (
              <div className="flex flex-wrap gap-1.5" data-testid="dataset-columns">
                {active.columns.map((c) => (
                  <Badge key={c.name} variant="secondary" className="text-[10px]">
                    {c.name} · {c.kind}{c.missing ? ` · ${c.missing} missing` : ''}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-2" data-testid="stats-result">
            <div className="text-sm font-medium">{result.caption}</div>
            <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-64">{result.markdown}</pre>
            <p className="text-[11px] text-muted-foreground italic">{result.note}</p>
            <Button onClick={handleSaveAsTable} disabled={saving} variant="outline"
              data-testid="stats-save-table-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                : <><Save className="w-4 h-4 mr-2" />Save as numbered table</>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
